import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Users, User, CheckCircle2, Clock, XCircle, ChevronUp, ChevronDown, QrCode, Check, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { getCurrentUser } from '../../profile/services';
import { apiCall } from '../../services/apiUtil';
import toast from 'react-hot-toast';
import PlayerQRScanModal from './PlayerQRScanModal';
import { btn } from '../../utils/buttonStyles';

/**
 * EventAttendance Component (Self-Contained)
 *
 * Displays and manages member attendance for an event.
 * All state and handlers are internal to this component.
 *
 * Props:
 * - voProvider: SquadDetailsProvider instance to read/update shared data
 * - event: Event object with responses array
 *
 * The component automatically shows warning styling when no members are available.
 *
 * Helper function:
 * - getAvailableMembers(members, event): Returns array of { id, name } for available members
 */

import { ApplyToSeriesConfirm } from './SeriesScopeModals';
import { isSquadManager } from '../../squads/utils/squadRoles';


const isDeletedMember = (member) => {
  return !!(member?.isDeletedUser || member?.is_deleted || member?.status === 'deleted');
};

export const getAvailableMembers = (members, event) => {
  if (!members || !event?.responses) return [];

  return members
    .filter(member => {
      if (isDeletedMember(member)) return false;

      const memberId = member.userId || member.user?.id || member.id;
      const response = event.responses.find(r => String(r.userId) === String(memberId));
      return response?.status === 'available' || response?.status === 'join-late';
    })
    .map(member => {
      const memberId = member.userId || member.user?.id || member.id;
      const firstName = member.firstName || member.first_name || member.user?.firstName || member.user?.first_name || '';
      const lastName = member.lastName || member.last_name || member.user?.lastName || member.user?.last_name || '';
      const memberName = (firstName && lastName)
        ? `${firstName} ${lastName}`
        : firstName || lastName || member.name || member.username || member.user?.username || `User ${memberId}`;
      return {
        id: memberId,
        name: memberName,
        status: getMemberStatusFromEvent(memberId, event)
      };
    });
};

const getMemberStatusFromEvent = (memberId, event) => {
  if (!event?.responses) return 'no-response';
  const response = event.responses.find(r => String(r.userId) === String(memberId));
  // An invitation nobody has answered yet is no answer.
  const status = response?.status;
  return !status || status === 'invited' ? 'no-response' : status;
};

/** Generate the first 3 arrival time slots (30-min intervals) starting from event start_time */
function generateArrivalSlots(event) {
  if (!event?.start_time) return [];
  const start = new Date(event.start_time);
  return [0, 1, 2].map(i => new Date(start.getTime() + i * 30 * 60 * 1000));
}

/** Format a Date to a readable time string like "6:30 PM" */
function formatTime(date) {
  return new Date(date).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
}

/** Arrival time distribution grouped by slot */
function ArrivalTimeSummary({ responses, members, event, theme }) {
  const slots = useMemo(() => generateArrivalSlots(event), [event]);
  if (slots.length === 0) return null;

  // Build a map: slotISO → [memberNames]
  const slotMap = {};
  slots.forEach(s => { slotMap[s.toISOString()] = []; });

  responses.forEach(r => {
    if (!r.arrivalTime) return;
    const arrivalMs = new Date(r.arrivalTime).getTime();
    // Find nearest slot
    const nearest = slots.reduce((best, s) =>
      Math.abs(s.getTime() - arrivalMs) < Math.abs(best.getTime() - arrivalMs) ? s : best
    );
    const key = nearest.toISOString();
    if (slotMap[key] !== undefined) {
      const member = members.find(m => {
        const mid = m.userId || m.user?.id || m.id;
        return String(mid) === String(r.userId);
      });
      const firstName = member?.firstName || member?.first_name || '';
      const lastName = member?.lastName || member?.last_name || '';
      const name = (firstName && lastName) ? `${firstName} ${lastName}` : firstName || lastName || 'Member';
      slotMap[key].push(name);
    }
  });

  const activeSlots = slots.filter(s => slotMap[s.toISOString()].length > 0);
  if (activeSlots.length === 0) return null;

  const eventStart = new Date(event.start_time);

  return (
    <div className={`mx-4 mb-3 p-3 rounded-lg ${theme.backgroundSecondary} border ${theme.border}`}>
      <div className={`text-xs font-semibold mb-2 ${theme.textSecondary} uppercase tracking-wide`}>
        Arrival Breakdown
      </div>
      <div className="flex flex-wrap gap-2">
        {activeSlots.map(slot => {
          const names = slotMap[slot.toISOString()];
          const isOnTime = slot.getTime() <= eventStart.getTime();
          return (
            <div
              key={slot.toISOString()}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                isOnTime
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
              }`}
              title={names.join(', ')}
            >
              {isOnTime ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
              <span>{formatTime(slot)}</span>
              <span className={`rounded-full px-1 ${
                isOnTime
                  ? 'bg-green-200 dark:bg-green-800'
                  : 'bg-yellow-200 dark:bg-yellow-800'
              }`}>
                {names.length}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const EventAttendance = ({
  voProvider,
  event,
  onEventUpdated,
  isGameActive = false
}) => {
  const { theme } = useTheme();
  const currentUser = getCurrentUser();
  const currentUserId = currentUser?.id;

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [localResponses, setLocalResponses] = useState(event?.responses || []);
  const [seriesConfirm, setSeriesConfirm] = useState({ open: false, pendingArgs: null });

  const isEventCreator = useMemo(() => {
    return event && currentUserId && event.createdBy === currentUserId;
  }, [event, currentUserId]);

  const isSquadAdmin = useMemo(() => {
    if (!voProvider) return false;
    const group = voProvider.getGroup();
    return isSquadManager(group?.userRole);
  }, [voProvider]);

  const isGameCompleted = useMemo(() => {
    return event?.status === 'completed';
  }, [event?.status]);

  const isAttendanceLocked = isGameActive || isGameCompleted;

  useEffect(() => {
    if (voProvider) {
      const voMembers = voProvider.getMembers();
      if (voMembers && voMembers.length > 0) {
        const activeMembers = voMembers.filter(m => !isDeletedMember(m) && (m.userId || m.user?.id));
        // An event with an invite list is for the people on it; an older event
        // is for the whole squad, as it always was.
        if (event?.audience === 'invitees') {
          const invited = new Set((event.responses || []).map(r => String(r.userId)));
          setMembers(activeMembers.filter(m => invited.has(String(m.userId || m.user?.id))));
        } else {
          setMembers(activeMembers);
        }
      }
    }
  }, [voProvider, event?.audience, event?.responses]);

  useEffect(() => {
    if (event?.responses) {
      setLocalResponses(event.responses);
    }
  }, [event?.responses]);

  if (!voProvider) {
    return null;
  }

  const group = voProvider.getGroup();

  const availableCount = useMemo(() => {
    if (!members || !localResponses) return 0;
    const activeMembers = members.filter(m => !isDeletedMember(m));
    return activeMembers.filter(member => {
      const actualUserId = member.userId || member.user?.id || member.id;
      const response = localResponses.find(r => String(r.userId) === String(actualUserId));
      return response?.status === 'available' || response?.status === 'join-late';
    }).length;
  }, [members, localResponses]);

  const getMemberStatus = (memberId) => {
    const response = localResponses.find(r => String(r.userId) === String(memberId));
    const status = response?.status;
    return !status || status === 'invited' ? 'no-response' : status;
  };

  const getMemberArrivalTime = (memberId) => {
    const response = localResponses.find(r => String(r.userId) === String(memberId));
    return response?.arrivalTime || null;
  };

  const handleStatusChange = useCallback(async (status, memberId, isCurrentUser, arrivalTime = null, applyToSeries = false) => {
    if (!event?.id) return;

    // If this is a recurring event and it's the current user changing their own status, prompt for series
    if (event.recurrenceRuleId && isCurrentUser && !applyToSeries) {
      setSeriesConfirm({ open: true, pendingArgs: { status, memberId, isCurrentUser, arrivalTime } });
      return;
    }

    try {
      const payload = { status };
      if (!isCurrentUser) payload.userId = memberId;
      if (arrivalTime !== undefined) payload.arrivalTime = arrivalTime;
      if (applyToSeries) payload.applyToSeries = true;

      const result = await apiCall(`/events/${event.id}/responses`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      const targetUserId = result.userId;
      const newResponse = {
        userId: targetUserId,
        user: result.user,
        status: result.status,
        arrivalTime: result.arrivalTime || null
      };

      setLocalResponses(prev => {
        const idx = prev.findIndex(r => String(r.userId) === String(targetUserId));
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = newResponse;
          return updated;
        }
        return [...prev, newResponse];
      });

      const updatedResponses = localResponses.map(r =>
        String(r.userId) === String(targetUserId) ? newResponse : r
      );
      if (!updatedResponses.some(r => String(r.userId) === String(targetUserId))) {
        updatedResponses.push(newResponse);
      }

      const updatedEvent = { ...event, responses: updatedResponses };
      voProvider.updateEvent(updatedEvent);

      if (onEventUpdated) {
        onEventUpdated(updatedEvent);
      }

      toast.success('Attendance updated successfully');
    } catch (err) {
      console.error('Error updating event response:', err);
      toast.error(err.message || 'Failed to update attendance');
    }
  }, [event, currentUserId, currentUser, voProvider, onEventUpdated, localResponses]);

  const handleSeriesConfirm = useCallback((applyToSeries) => {
    const args = seriesConfirm.pendingArgs;
    setSeriesConfirm({ open: false, pendingArgs: null });
    if (args) handleStatusChange(args.status, args.memberId, args.isCurrentUser, args.arrivalTime, applyToSeries);
  }, [seriesConfirm, handleStatusChange]);

  const handleMarkAllAvailable = useCallback(async () => {
    if (!event?.id || !members || members.length === 0) return;

    setLoading(true);
    try {
      const activeMembers = members.filter(m => !isDeletedMember(m));

      const updatePromises = activeMembers.map(member => {
        const memberId = member.userId || member.user?.id || member.id;
        const isCurrentUserRow = memberId === currentUserId;

        const payload = { status: 'available' };
        if (!isCurrentUserRow) {
          payload.userId = memberId;
        }

        return apiCall(`/events/${event.id}/responses`, {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      });

      await Promise.all(updatePromises);

      const freshEvent = await apiCall(`/events/${event.id}`);

      const transformedEvent = {
        ...freshEvent,
        responses: (freshEvent.responses || []).map(r => ({
          userId: r.userId,
          user: r.user,
          username: r.user?.first_name && r.user?.last_name
            ? `${r.user.first_name} ${r.user.last_name}`
            : `User ${r.userId}`,
          status: r.status,
          arrivalTime: r.arrivalTime || null
        }))
      };

      voProvider.updateEvent(transformedEvent);

      if (onEventUpdated) {
        onEventUpdated(transformedEvent);
      }

      toast.success('All members marked as available');
    } catch (err) {
      console.error('Error marking all available:', err);
      toast.error('Failed to mark all members as available');
    } finally {
      setLoading(false);
    }
  }, [event, members, currentUserId, voProvider, onEventUpdated]);

  const handleQRAttendanceMarked = useCallback(async () => {
    if (!event?.id) return;
    try {
      const freshEvent = await apiCall(`/events/${event.id}`);
      const transformedEvent = {
        ...freshEvent,
        responses: (freshEvent.responses || []).map(r => ({
          userId: r.userId,
          user: r.user,
          username: r.user?.first_name && r.user?.last_name
            ? `${r.user.first_name} ${r.user.last_name}`
            : `User ${r.userId}`,
          status: r.status,
          arrivalTime: r.arrivalTime || null
        }))
      };
      voProvider.updateEvent(transformedEvent);
      if (onEventUpdated) onEventUpdated(transformedEvent);
    } catch (err) {
      console.error('Error refreshing event after QR scan:', err);
    }
  }, [event, voProvider, onEventUpdated]);

  if (!event) return null;

  const showWarningStyle = availableCount === 0;

  return (
    <div className={`mb-4 ${theme.card} rounded-xl border ${showWarningStyle ? 'border-amber-400 border-2' : theme.border} overflow-hidden`}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full p-4 ${showWarningStyle ? 'bg-amber-50 dark:bg-amber-900/20' : theme.backgroundSecondary} hover:opacity-90 transition-opacity`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-x-2">
            <Users className={`w-5 h-5 ${showWarningStyle ? 'text-amber-600' : ''}`} />
            <span className={`font-semibold ${showWarningStyle ? 'text-amber-800 dark:text-amber-400' : theme.text}`}>
              Event Attendance (<span className={availableCount > 0 ? 'text-green-600' : 'text-amber-600'}>{availableCount}/{members?.length || 0}</span> available)
              {showWarningStyle && (
                <span className="ml-2 text-amber-600 text-sm">← Set availability here</span>
              )}
            </span>
          </div>
          {isExpanded ? (
            <ChevronUp className={`w-5 h-5 ${showWarningStyle ? 'text-amber-600' : theme.text}`} />
          ) : (
            <ChevronDown className={`w-5 h-5 ${showWarningStyle ? 'text-amber-600' : theme.text}`} />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="overflow-x-auto">
          {/* Lock banner when a game is in progress */}
          {isGameActive && !isGameCompleted && (
            <div className="flex items-center gap-2 mx-4 mt-3 mb-1 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-400 text-sm">
              <span>🔒</span>
              <span>Attendance is locked — a game is in progress.</span>
            </div>
          )}


          {/* Arrival time distribution summary */}
          {localResponses.some(r => r.arrivalTime) && (
            <div className="pt-3">
              <ArrivalTimeSummary
                responses={localResponses}
                members={members}
                event={event}
                theme={theme}
              />
            </div>
          )}

          {/* Action Buttons */}
          {!isAttendanceLocked && members && members.length > 0 && (
            <div className="flex justify-end gap-2 mb-2 px-4 pt-2">
              <button
                type="button"
                onClick={() => setShowQRScanner(true)}
                className={`flex items-center gap-x-1 px-3 py-1.5 border ${theme.border} hover:bg-gray-50 dark:hover:bg-gray-800 text-sm rounded-lg transition-colors`}
                title="Scan your profile QR code to mark yourself available"
              >
                <QrCode className="w-4 h-4" />
                <span>Scan My QR</span>
              </button>

              {(isEventCreator || isSquadAdmin) && (
                <button
                  type="button"
                  onClick={handleMarkAllAvailable}
                  disabled={loading}
                  className={`${btn.base} ${btn.sm} ${theme.buttonPrimary} flex items-center gap-x-1 disabled:opacity-50 disabled:cursor-not-allowed`}
                  title="Mark all members as available"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{loading ? 'Updating...' : 'Mark All Available'}</span>
                </button>
              )}
            </div>
          )}

          {/* Members Table */}
          <table className="w-full">
            <thead className={theme.backgroundSecondary}>
              <tr>
                <th className={`text-left py-3 px-4 font-semibold ${theme.text}`}>Member</th>
                <th className={`text-left py-3 px-4 font-semibold ${theme.text}`}>Status</th>
                <th className={`text-left py-3 px-4 font-semibold ${theme.text}`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members && members.length > 0 ? members.map((member) => {
                const actualUserId = member.userId || member.user?.id;
                const memberName = member.firstName && member.lastName
                  ? `${member.firstName} ${member.lastName}`
                  : member.username || `User ${actualUserId}`;
                const status = getMemberStatus(actualUserId);
                const arrivalTime = getMemberArrivalTime(actualUserId);
                const isCurrentUserRow = String(actualUserId) === String(currentUserId);
                const canEditRow = isEventCreator || isSquadAdmin || isCurrentUserRow;

                return (
                  <tr key={actualUserId} className={`border-t ${theme.border}`}>
                    {/* Member Name */}
                    <td className={`py-3 px-4 ${theme.text}`}>
                      <div className="flex items-center gap-x-2">
                        <User className="w-4 h-4" />
                        <span>{memberName}</span>
                      </div>
                    </td>

                    {/* Status Badge */}
                    <td className="py-3 px-4">
                      {status === 'available' && (
                        <span className="inline-flex items-center gap-x-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                          <CheckCircle2 className="w-3 h-3" />
                          <span>Available</span>
                          {arrivalTime && (
                            <span className="ml-1 opacity-75">· {formatTime(arrivalTime)}</span>
                          )}
                        </span>
                      )}
                      {status === 'join-late' && (
                        <span className="inline-flex items-center gap-x-1 px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                          <Clock className="w-3 h-3" />
                          <span>Late</span>
                          {arrivalTime && (
                            <span className="ml-1 opacity-75">· {formatTime(arrivalTime)}</span>
                          )}
                        </span>
                      )}
                      {status === 'unavailable' && (
                        <span className="inline-flex items-center gap-x-1 px-2 py-1 rounded-full text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                          <XCircle className="w-3 h-3" />
                          <span>Unavailable</span>
                        </span>
                      )}
                      {status === 'no-response' && (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${theme.backgroundSecondary} ${theme.textSecondary}`}>
                          No Response
                        </span>
                      )}
                    </td>

                    {/* Available / Unavailable icon buttons */}
                    <td className="py-3 px-4">
                      {!isAttendanceLocked && canEditRow && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            title="Mark Available"
                            disabled={status === 'available' || status === 'join-late'}
                            onClick={() => handleStatusChange('available', actualUserId, isCurrentUserRow, null)}
                            className={`p-1.5 rounded-lg border transition-colors ${
                              status === 'available' || status === 'join-late'
                                ? 'bg-green-500 text-white border-green-500 opacity-50 cursor-not-allowed'
                                : 'border-gray-400 text-gray-300 hover:border-green-400 hover:text-green-400'
                            }`}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            title="Mark Unavailable"
                            disabled={status === 'unavailable'}
                            onClick={() => handleStatusChange('unavailable', actualUserId, isCurrentUserRow, null)}
                            className={`p-1.5 rounded-lg border transition-colors ${
                              status === 'unavailable'
                                ? 'bg-red-500 text-white border-red-500 opacity-50 cursor-not-allowed'
                                : 'border-gray-400 text-gray-300 hover:border-red-400 hover:text-red-400'
                            }`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="3" className={`py-6 text-center ${theme.textSecondary}`}>
                    No members found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <p className={`px-4 py-2 text-xs ${theme.textMuted}`}>
            * Invited members who haven't joined the app yet are not shown and will not be counted as available.
          </p>
        </div>
      )}

      {/* QR Scanner Modal */}
      <PlayerQRScanModal
        isOpen={showQRScanner}
        onClose={() => setShowQRScanner(false)}
        event={event}
        onAttendanceMarked={handleQRAttendanceMarked}
      />

      <ApplyToSeriesConfirm
        isOpen={seriesConfirm.open}
        onConfirm={handleSeriesConfirm}
        onCancel={() => setSeriesConfirm({ open: false, pendingArgs: null })}
      />
    </div>
  );
};

export default EventAttendance;
